import { v4 } from 'uuid';
import localeCode from 'locale-code';
import { COOKIE_KEYS, CustomDataDto, normalizeCountry, normalizeEmail, normalizeGender, normalizeName, normalizePhone, sha256Hash, UserData } from './utils';
import { post } from '../../networking';
import { isEmpty } from 'lodash';
import dataDogLogger from '../datadog';

const ENVIRONMENT = process.env.NEXT_PUBLIC_NENV;
const trackingEnvironments = ['production'];

export const SNAP_PIXEL_ID = 'dc5bb615-96fd-49b9-8a97-42bd967f5d60';

export enum SnapEventName {
    PAGE_VIEW = 'PAGE_VIEW',
    START_CHECKOUT = 'START_CHECKOUT',
    PURCHASE = 'PURCHASE',
    SUBSCRIBE = 'SUBSCRIBE',
    SIGN_UP = 'SIGN_UP',
    CUSTOM_EVENT_2 = 'CUSTOM_EVENT_2', // M2 ($50 provider review)
    CUSTOM_EVENT_1 = 'CUSTOM_EVENT_1' // M1 ($297 naltrexone intake subscription)
}

type SnapchatTrackingUserData = UserData & {
    sc_click_id?: string,
    sc_cookie1?: string
}

type SnapchatClientSideTrackingParams = {
    client_deduplication_id: string, // must match serverSide: custom_data.event_id
    user_hashed_email: string,
    user_hashed_phone_number: string,
    firstname: string,
    lastname: string,
    geo_country: string,
    uuid_c1?: string, // _scid cookie value
    click_id?: string, // snapchat click id (ss_snapClickId cookie value)
    currency?: string, // ISO 4217 currency code
    price?: number,
    transaction_id?: string
};

export type SnapchatServerSideTrackingParams = {
    event_id: string // must match clientSide: client_deduplication_id
    event_name: string,
    event_time: number,
    action_source: 'WEB',
    event_source_url: string,
    user_data: SnapchatTrackingUserData,
    custom_data?: CustomDataDto,
    sc_click_id?: string, // snapchat click id (ss_snapClickId cookie value)
    sc_cookie1?: string // _scid cookie value
}

type SnapchatEventParams = {
    event_name: string,
    user: any,
    url: string,
    cookies?: any,
    custom_data?: CustomDataDto
}

const snapClientSideProxy = (
    event_name: string,
    payload: SnapchatClientSideTrackingParams
) => {
    const snaptr = window && window['snaptr'];
    if(typeof snaptr !== 'function') {
        dataDogLogger('error', `Snapchat Pixel not initialized when firing event ${event_name}`);
        return;
    }

    if(!trackingEnvironments.includes(ENVIRONMENT)) {
        console.log('Snapchat Pixel', event_name, payload);
        return;
    }

    snaptr(
        'track',
        event_name,
        payload
    );
};

const snapServerSideProxy = async (payload: SnapchatServerSideTrackingParams) => {
    if(!trackingEnvironments.includes(ENVIRONMENT)) {
        console.log('Snapchat CAPI', payload.event_name, payload);
        return;
    }

    await post('tracking/snapchat', payload);
};

export default async ({
    event_name,
    user,
    url,
    cookies,
    custom_data = {}
}: SnapchatEventParams) => {
    if (!user) {
        return;
    }

    const event_id = v4();
    const locale = Intl.DateTimeFormat().resolvedOptions().locale;
    const countryCode = localeCode.getCountryCode(locale);

    const user_data: SnapchatTrackingUserData = {
        em: sha256Hash(normalizeEmail(user.email)),
        ph: sha256Hash(normalizePhone(user.phone)),
        fn: sha256Hash(normalizeName(user.givenName)),
        ln: sha256Hash(normalizeName(user.familyName)),
        ge: sha256Hash(normalizeGender(user.gender)),
        external_id: sha256Hash(user.id),
        country: sha256Hash(normalizeCountry(countryCode)),
        client_user_agent: window.navigator.userAgent
    };

    const clientSidePayload: SnapchatClientSideTrackingParams = {
        client_deduplication_id: event_id,
        user_hashed_email: user_data.em,
        user_hashed_phone_number: user_data.ph,
        firstname: user_data.fn,
        lastname: user_data.ln,
        geo_country: user_data.country,
        ...(custom_data.value ? { price: custom_data.value } : {}),
        ...(custom_data.currency ? { currency: custom_data.currency } : {}),
        ...(event_name === SnapEventName.PURCHASE ? { transaction_id: event_id } : {}),
        ...(custom_data.product_line ? { product_line: custom_data.product_line } : {}),
        ...(custom_data.funnel_id ? { funnel_id: custom_data.funnel_id } : {})
    };

    const serverSidePayload: SnapchatServerSideTrackingParams = {
        event_id,
        event_name: event_name,
        event_time: Date.now(),
        action_source: 'WEB',
        user_data,
        event_source_url: url
    };
    if (!isEmpty(custom_data)) serverSidePayload.custom_data = custom_data;

    // insert sc_click_id and sc_cookie1 if they exist
    if(cookies) {
        if (cookies[COOKIE_KEYS.SNAP_CLICK_ID]) {
            clientSidePayload.click_id = cookies[COOKIE_KEYS.SNAP_CLICK_ID];
            serverSidePayload.sc_click_id = cookies[COOKIE_KEYS.SNAP_CLICK_ID];
        }
        if (cookies[COOKIE_KEYS.SNAP_COOKIE1]) {
            clientSidePayload.uuid_c1 = cookies[COOKIE_KEYS.SNAP_COOKIE1];
            serverSidePayload.sc_cookie1 = cookies[COOKIE_KEYS.SNAP_COOKIE1];
        }
    }

    snapClientSideProxy(event_name, clientSidePayload);
    await snapServerSideProxy(serverSidePayload);
};
